import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ChevronRight, CircleHelp, Search } from "lucide-react";
import { Modal } from "../../components/Modal";
import { api } from "../../api";
import { bereichFuerPfad, type HilfeDaten, type HilfeEintrag } from "./inhalte";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Der Pfad, auf dem der Leser gerade steht — bestimmt den Startbereich. */
  pfad: string;
};

/** Ein Treffer der Suche: die Frage samt dem Bereich, aus dem sie stammt. */
type Treffer = {
  schluessel: string;
  bereich: string;
  frage: string;
  antwort: string;
};

/** Alle Fragen aller Bereiche als flache Liste — gebaut, sobald der Text da
 *  ist, danach nur noch gefiltert. Die Sammlung ist klein (gut hundert
 *  Einträge), deshalb reicht ein schlichter Teilstring-Vergleich; ein
 *  Rangwerk wie in der Befehlspalette wäre hier Aufwand ohne Gewinn. */
function alleFragen(daten: HilfeDaten | null): Treffer[] {
  if (!daten) return [];
  return daten.order.flatMap((schluessel) => {
    const eintrag = daten.areas[schluessel];
    if (!eintrag) return [];
    return eintrag.abschnitte.map((a) => ({
      schluessel,
      bereich: eintrag.titel,
      frage: a.frage,
      antwort: a.antwort,
    }));
  });
}

/** Der Text, einmal geholt und dann behalten.
 *
 *  Er liegt beim Server (siehe inhalte.ts) und kommt erst, wenn jemand die
 *  Hilfe aufschlägt — wer sie nie öffnet, lädt ihn nie. Zwischen zwei
 *  Aufrufen bleibt er im Speicher: derselbe Text zweimal zu holen, nur weil
 *  das Fenster zwischendurch zu war, wäre Verschwendung. */
let zwischenspeicher: HilfeDaten | null = null;
let unterwegs: Promise<HilfeDaten> | null = null;

function hilfeHolen(): Promise<HilfeDaten> {
  if (zwischenspeicher) return Promise.resolve(zwischenspeicher);
  if (!unterwegs) {
    unterwegs = api<HilfeDaten>("/help/content")
      .then((d) => { zwischenspeicher = d; return d; })
      .finally(() => { unterwegs = null; });
  }
  return unterwegs;
}

/** Ein Schritt, dessen Befehle als Befehle dastehen.
 *
 *  Eine Zeile, die mit vier Leerzeichen beginnt, ist einer. Sie gehört in
 *  feste Breite und auf eine eigene Zeile — im Fließtext ist ein Befehl
 *  etwas, das man abtippen muss statt es zu kopieren, und ein Bindestrich
 *  am Zeilenumbruch macht daraus einen falschen Befehl. */
function mitBefehlen(text: string): ReactNode {
  const zeilen = text.split("\n");
  if (zeilen.length === 1) return text;
  return zeilen.map((z, i) =>
    z.startsWith("    ")
      ? <code className="hilfe-befehl" key={i}>{z.trim()}</code>
      : <span key={i}>{z}</span>,
  );
}

function passt(t: Treffer, q: string): boolean {
  return (
    t.frage.toLowerCase().includes(q) ||
    t.antwort.toLowerCase().includes(q) ||
    t.bereich.toLowerCase().includes(q)
  );
}

/**
 * Die Hilfe zur Seite, auf der man steht.
 *
 * Sie öffnet immer beim passenden Bereich, lässt sich aber auf jeden anderen
 * umstellen: wer auf den Noten steht und wissen will, was ein Commit ist,
 * soll dafür die Seite nicht wechseln müssen. Die Suche geht quer über alles.
 */
export function HilfePanel({ open, onClose, pfad }: Props) {
  const [daten, setDaten] = useState<HilfeDaten | null>(zwischenspeicher);
  const [ladeFehler, setLadeFehler] = useState(false);
  const startBereich = bereichFuerPfad(pfad, daten?.order ?? []);
  const [bereich, setBereich] = useState<string>(startBereich);
  const [suche, setSuche] = useState("");
  /** Welche Frage gerade aufgeklappt ist. Eine reicht: wer zwei Antworten
   *  nebeneinander braucht, hat die Hilfe schon verlassen. */
  const [offen, setOffen] = useState<number | null>(null);
  // Ein anderer Bereich ist eine andere Liste; eine Frage, die dort offen
  // stünde, wäre eine fremde.
  useEffect(() => { setOffen(null); }, [bereich]);
  const koerperRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);

  // Bei jedem Öffnen zurück auf die aktuelle Seite und mit leerer Suche —
  // eine Hilfe, die noch den Rest der letzten Sitzung zeigt, beantwortet
  // die Frage nicht, die man gerade hat.
  useEffect(() => {
    if (!open) return;
    setBereich(startBereich);
    setSuche("");
  }, [open, startBereich]);

  // Der Text kommt vom Server und erst beim Aufschlagen — siehe inhalte.ts.
  useEffect(() => {
    if (!open || daten) return;
    let tot = false;
    hilfeHolen()
      .then((d) => { if (!tot) { setDaten(d); setLadeFehler(false); } })
      .catch(() => { if (!tot) setLadeFehler(true); });
    return () => { tot = true; };
  }, [open, daten]);

  // Beim Wechsel des Bereichs wieder nach oben: sonst landet man mitten im
  // neuen Text, weil der alte länger war.
  useEffect(() => {
    const el = koerperRef.current;
    el?.scrollTo({ top: 0 });
    // Auf dem Telefon rollt nicht dieser Kasten, sondern der Rumpf des
    // Fensters — beide anstoßen ist billiger als zu raten, welcher gerade gilt.
    (el?.closest(".modal-body") as HTMLElement | null)?.scrollTo({ top: 0 });
    // Auf dem Telefon ist die Bereichsliste eine Zeile, die seitlich rollt.
    // Steht der aktuelle Bereich weit hinten, sieht man sonst nicht, wo man
    // gerade ist — also ihn ins Bild holen.
    navRef.current
      ?.querySelector<HTMLElement>(".hilfe-bereich.ist-an")
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [bereich, open]);

  const q = suche.trim().toLowerCase();
  const treffer = useMemo(
    () => (q ? alleFragen(daten).filter((t) => passt(t, q)) : []),
    [q, daten],
  );

  const leer: HilfeEintrag = { titel: "Help", kurz: "", abschnitte: [] };
  const eintrag: HilfeEintrag =
    daten?.areas[bereich] ?? daten?.areas["/"] ?? leer;
  const reihenfolge = daten?.order ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <span className="hilfe-titel">
          <CircleHelp className="icon-svg" />
          <span>Help · {eintrag.titel}</span>
        </span>
      }
    >
      <div className="hilfe">
        {/* Die Bereichsliste: links auf dem Bildschirm, oben als Zeile auf
            dem Telefon — dieselben Knöpfe, nur anders gelegt (siehe CSS). */}
        <nav className="hilfe-bereiche" aria-label="Help areas" ref={navRef}>
          {reihenfolge.map((s) => (
            <button
              key={s}
              type="button"
              className={`hilfe-bereich${s === bereich && !q ? " ist-an" : ""}`}
              onClick={() => {
                setSuche("");
                setBereich(s);
              }}
            >
              {daten?.areas[s]?.titel ?? s}
            </button>
          ))}
        </nav>

        <div className="hilfe-inhalt">
          {/* Die Suche bleibt oben stehen. Sie ist der Weg zu allem, was
              nicht in diesem Bereich steht — mit nach unten zu scrollen
              wäre sie genau dann weg, wenn man sie braucht. */}
          <div className="hilfe-suche">
            <Search className="icon-svg" />
            <input
              type="search"
              value={suche}
              placeholder="Search all help…"
              onChange={(e) => setSuche(e.target.value)}
              spellCheck={false}
              autoComplete="off"
              aria-label="Search all help"
            />
          </div>

          <div className="hilfe-rollen" ref={koerperRef}>
          {!daten ? (
            <p className="hilfe-leer">
              {ladeFehler
                ? "The help could not be loaded. Sign in and try again."
                : "Loading…"}
            </p>
          ) : q ? (
            treffer.length === 0 ? (
              <p className="hilfe-leer">
                Nothing matches “{suche.trim()}”. Try a single word — “token”,
                “branch”, “share”, “trash”.
              </p>
            ) : (
              <div className="hilfe-treffer">
                <p className="hilfe-treffer-zahl">
                  {treffer.length} {treffer.length === 1 ? "answer" : "answers"}
                </p>
                {treffer.map((t, i) => (
                  <article className="hilfe-frage" key={`${t.schluessel}-${i}`}>
                    <button
                      type="button"
                      className="hilfe-herkunft"
                      onClick={() => {
                        setSuche("");
                        setBereich(t.schluessel);
                      }}
                    >
                      {t.bereich}
                    </button>
                    <h3>{t.frage}</h3>
                    <p>{mitBefehlen(t.antwort)}</p>
                  </article>
                ))}
              </div>
            )
          ) : (
            <>
              <p className="hilfe-kurz">{eintrag.kurz}</p>

              {eintrag.schritte && eintrag.schritte.length > 0 && (
                <section className="hilfe-schritte">
                  <h3>{eintrag.schritteTitel ?? "Step by step"}</h3>
                  <ol>
                    {eintrag.schritte.map((s, i) => (
                      <li key={i}>{mitBefehlen(s)}</li>
                    ))}
                  </ol>
                </section>
              )}

              {/* Die Fragen stehen zu, die Antworten kommen auf Klick.
                  Vorher lagen alle zwölf Antworten eines Bereichs
                  untereinander offen da — eine Wand aus Text, in der man
                  die eine Frage suchen musste, die man hatte. Zugeklappt
                  ist die Hilfe das, was sie sein soll: eine Liste von
                  Fragen. Beim Suchen bleiben die Treffer offen, sonst
                  müsste man nach dem Suchen noch einmal klicken. */}
              {eintrag.abschnitte.map((a, i) => {
                const auf = offen === i;
                return (
                  <article className={`hilfe-frage${auf ? " ist-auf" : ""}`} key={i}>
                    <button
                      type="button"
                      className="hilfe-frage-kopf"
                      aria-expanded={auf}
                      onClick={() => setOffen(auf ? null : i)}
                    >
                      <ChevronRight className="icon-svg hilfe-pfeil" />
                      <h3>{a.frage}</h3>
                    </button>
                    {auf && <p>{mitBefehlen(a.antwort)}</p>}
                  </article>
                );
              })}
            </>
          )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
